import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface PageData {
  id?: string;
  title: string;
  slug: string;
  category: string;
  status: string;
  metaDescription: string;
  content: string;
}

interface PageModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (page: PageData) => void;
  page?: PageData | null;
  mode: 'add' | 'edit';
}

const PageModal: React.FC<PageModalProps> = ({ show, onHide, onSave, page, mode }) => {
  const [formData, setFormData] = useState<PageData>({
    title: '',
    slug: '',
    category: 'Company',
    status: 'Draft',
    metaDescription: '',
    content: ''
  });

  // Quill editor modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image', 'video'],
      [{ 'align': [] }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'align', 'color', 'background'
  ];

  useEffect(() => {
    if (page && mode === 'edit') {
      setFormData({
        id: page.id,
        title: page.title || '',
        slug: page.slug || '',
        category: page.category || 'Company',
        status: page.status || 'Draft',
        metaDescription: page.metaDescription || '',
        content: page.content || ''
      });
    } else if (mode === 'add') {
      setFormData({
        title: '',
        slug: '',
        category: 'Company',
        status: 'Draft',
        metaDescription: '',
        content: ''
      });
    }
  }, [page, mode, show]);

  const handleInputChange = (field: keyof PageData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from title
    if (field === 'title') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'add' ? 'Create New Page' : 'Edit Page'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                  placeholder="Enter page title"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  <option value="Company">Company</option>
                  <option value="Legal">Legal</option>
                  <option value="Support">Support</option>
                  {/* <option value="Media">Media</option> */}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Slug *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  required
                  placeholder="page-url-slug"
                />
                <Form.Text className="text-muted">
                  URL: /pages/{formData.slug || 'your-slug'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="Draft">Draft</option>
                  <option value="Published">Published</option>
                  {/* <option value="Archived">Archived</option> */}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Meta Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.metaDescription}
              onChange={(e) => handleInputChange('metaDescription', e.target.value)}
              placeholder="Brief description for SEO (recommended 150-160 characters)"
              maxLength={160}
            />
            <Form.Text className="text-muted">
              {(formData.metaDescription || '').length}/160 characters
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Content *</Form.Label>
            <ReactQuill
              theme="snow"
              value={formData.content}
              onChange={(value) => handleInputChange('content', value)}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Enter page content..."
              style={{ height: '300px', marginBottom: '50px' }}
            />
            <Form.Text className="text-muted">
              Use the toolbar above for rich text formatting
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {mode === 'add' ? 'Create Page' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PageModal;

